import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { BusinessRounded } from "@mui/icons-material";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import { useTheme } from "@mui/material/styles";

import { UserAnalyticsService } from "@analytics/index";

import routes from "@constants/routes";

import { convertArrayToQueryString } from "@hooks/useFilters";

import { useGetContactsQuery } from "@queries/contact.query";

import ContactService from "@services/contact.service";

import { useTableStore } from "@store/filters/filters.store";

const useDirectorySlideStepper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { selection: selectedCompanyIds } = useTableStore("company");
  const { selection: selectedContactIds } = useTableStore("contact");

  const { data: contacts } = useGetContactsQuery();

  const slides = useMemo(
    () => [
      {
        backButtonTooltip: "",
        handleBack: () => {
          /* Do Nothing */
        },
        handleNext: () => {
          if (selectedCompanyIds.length > 20) return;
          const urlSafeIds = convertArrayToQueryString("companyIds", selectedCompanyIds);
          navigate(`${routes.EMBEDDING_SEARCH_CONTACTS}?${urlSafeIds}`);
        },
        isBackButtonDisabled: true,
        isNextButtonDisabled: selectedCompanyIds.length === 0,
        name: "Select Companies",
        nextButtonStyle:
          selectedCompanyIds.length > 20
            ? {
                "&:hover": {
                  backgroundColor: theme.palette.error.dark,
                  color: theme.palette.error.contrastText,
                },
                backgroundColor: theme.palette.error.main,
                cursor: "not-allowed",
              }
            : {},
        nextButtonText: "Get Contacts",
        nextButtonTooltip:
          selectedCompanyIds.length === 0
            ? "Select one or more companies to view their contacts."
            : selectedCompanyIds.length > 20
              ? "You can request contacts for up to 20 companies at a time."
              : "Get contacts for selected companies.",
        splatString: "companies",
      },
      {
        backButtonTooltip: "View company directory.",
        handleBack: () => {
          navigate(routes.EMBEDDING_SEARCH_COMPANIES);
        },
        handleNext: () => {
          void ContactService.exportContacts(selectedContactIds, contacts?.contacts || []);
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: true,
        isNextButtonPending: false,
        name: "Export Contacts",
        nextButtonText: "",
        nextButtonTooltip: "",
        showNextButton: false,
        splatString: "contacts",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompanyIds, navigate, selectedContactIds, contacts],
  );

  const slideIndex = useMemo(
    () => slides.findIndex((slide) => location.pathname.includes(slide.splatString)),
    [location.pathname, slides],
  );

  const breadcrumbs = useMemo(
    () => [
      {
        clickable: location.pathname !== routes.EMBEDDING_SEARCH_COMPANIES,
        icon: BusinessRounded,
        label: "Companies",
        onClick: () => {
          if (location.pathname === routes.EMBEDDING_SEARCH_CONTACTS) {
            navigate(routes.EMBEDDING_SEARCH_COMPANIES);
          }
        },
        tooltipText: "Browse all companies in the ScaleIP Index.",
      },
      {
        clickable: false,
        icon: PersonSearchRoundedIcon,
        label: "Contacts",
        onClick: () => {
          /* Do Nothing */
        },
        tooltipText: "Select companies to get contact information.",
      },
    ],
    [navigate],
  );

  return {
    breadcrumbs,
    slideIndex,
    slides,
  };
};

export default useDirectorySlideStepper;
